import React, { useState, useEffect } from 'react'
import ColumnView, { Column } from 'core-system/ColumnView'
import { useDispatch, useSelector } from 'react-redux'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { GrhActiveOptions } from 'redux/grh/grhTypes'
import { AppState } from 'redux/config/store'
import { grhActions } from 'redux/grh/grhSlice'
import GRHUtils from '../Shared/GRHUtils'
import SegmentService from 'redux/config/services/SegmentService'
import * as Program from 'core-system/Program'
import SegmentDropdownUtils from 'core-system/SegmentDropdown/SegmentDropdownUtils'
import Loading from 'core-system/Loading'
import ProgramOption from 'core-system/Program/ProgramOption'
import DateUtils from 'shared/DateUtils'
import useQueryParam from 'shared/Hooks/useQueryParam'
import BudgetUsesSelector from './components/BudgetUsesSelector'
import GRHActivationSummary from './components/GRHActivationSummary'
import FlexContainer from 'core-system/FlexContainer'
import Button from 'core-system/Button'
import ServiceProviderSelectorModal from 'core-system/ServiceProvider/ServiceProviderSelectorModal'
import moment from 'moment'
import { notificationsActions } from 'redux/notifications/notificationsSlice'

const defaultActiveOptions = {
  rideshare: true,
  localTaxi: true,
}

const nextMonth = () => {
  return {
    val: 'next-month',
    text: DateUtils.getNextXMonths(1, 1)[0].text,
  }
}

type LocationState = {
  from: string
  activeOptions: GrhActiveOptions
}

const GRHActivationView = React.memo(() => {
  const segmentId = useQueryParam('segmentId')
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const state = location.state as LocationState
  const segmentsMap = useSelector(
    (state: AppState) => state.employer.segmentsMap
  )
  const allSegments = useSelector(
    (state: AppState) => state.employer.allSegments
  )
  const merchantsMap = useSelector(
    (state: AppState) => state.programs.merchantsMap
  )

  const allGrhPrograms = useSelector((state: AppState) => state.grh.grhPrograms)
  const availableMerchants = useSelector(
    (state: AppState) => state.grh.availableMerchants
  )
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [startDate, setStartDate] = useState(nextMonth)
  const [transactionLimit, setTransactionLimit] = useState(
    GRHUtils.recTransacLimit
  )
  const [allowedUses, setAllowedUses] = useState(GRHUtils.recUses)
  const [errorState, setErrorState] = useState(GRHUtils.defaultErrorState)
  const [availableMerchantIds, setAvailableMerchantIds] = useState<string[]>([])
  const [activeRideShareIds, setActiveRideshareIds] = useState<string[]>([])
  const [activeLocalTaxiIds, setActiveLocalTaxiIds] = useState<string[]>([])

  const [activeOptions, setActiveOptions] = useState(
    state && state.activeOptions ? state.activeOptions : defaultActiveOptions
  )

  useEffect(() => {
    dispatch(
      grhActions.getGrhRecommendations({
        segmentId,
      })
    )
  }, [dispatch, segmentId])

  useEffect(() => {
    if (availableMerchants && merchantsMap) {
      const rideshareMerchantIds = availableMerchants.rideshare.map((m) => m.id)
      const localTaxiMerchantIds = availableMerchants.localTaxi.map((m) => m.id)

      setAvailableMerchantIds(rideshareMerchantIds)
      setActiveRideshareIds(rideshareMerchantIds)
      setActiveLocalTaxiIds(
        GRHUtils.getLocalTaxiProviders(
          localTaxiMerchantIds,
          [...availableMerchants.rideshare, ...availableMerchants.localTaxi],
          segmentsMap[segmentId].metroAreas
        )
      )
    }
  }, [availableMerchants, merchantsMap, segmentsMap, segmentId])

  const handleOptionToggle = (type: string) => {
    setActiveOptions((prevState) => {
      return {
        ...prevState,
        [type]: !prevState[type],
      }
    })
  }
  const handleProviderChange = (newActiveMerchants: string[]) => {
    setActiveRideshareIds(newActiveMerchants)
  }

  const handleDateChange = (val: string, text: string, initial: boolean) => {
    setStartDate(initial ? nextMonth : { val, text })
  }

  const handleOpenModal = () => {
    setIsModalOpen(true)
    SegmentService.track('modes-modal-toggle', {
      action: 'open',
      state: 'edit',
      location: 'grh-providers',
    })
  }

  const handleSegmentChange = (newSegmentId: string) => {
    if (newSegmentId !== segmentId) {
      navigate(`/guaranteed-rides/${newSegmentId}/activation`)
      SegmentService.track('baseOptions-toggle-action', {
        actionType: 'segment-name',
      })
    }
  }
  const handleActivate = () => {
    const checkErrors = {
      uses: !allowedUses && true,
      transactionLimit: !transactionLimit && true,
      rideshare:
        availableMerchants.rideshare.length > 0 &&
        activeRideShareIds.length === 0 &&
        activeOptions.rideshare &&
        true,
      localTaxi: false,
    }

    const hasNoErrors = Object.keys(checkErrors).every(
      (key) => !checkErrors[key]
    )
    if (hasNoErrors) {
      const startDateFormatted = moment(startDate.text, 'MMMM Do YYYY').format()

      const merchants = []
      if (activeOptions.rideshare) merchants.push(...activeRideShareIds)
      if (activeOptions.localTaxi) {
        const localTaxiIds = availableMerchants.localTaxi.map((m) => m.id)
        merchants.push(...localTaxiIds)
      }

      dispatch(
        grhActions.createGrhProgram({
          startDate: startDateFormatted,
          endDate: null,
          segment: segmentId,
          name: 'grh',
          type: 'GRH',
          budget: transactionLimit,
          numUses: allowedUses,
          requiresRequest: true,
          merchants: merchants,
          yearlyPool: true,
        })
      )

      SegmentService.track('programs-action-click', {
        action: 'activate',
        programType: 'grh',
        segmentName: segmentsMap[segmentId].name,
        startDate: moment(startDateFormatted).format('DD-MM-YYYY'),
        budget: transactionLimit,
        flexProviders:
          activeRideShareIds.length > 0 && activeOptions.rideshare
            ? activeRideShareIds.map((m) => merchantsMap[m]?.name)
            : false,
        gas: null,
        parking: null,
        transit: null,
        oneTime: null,
        shared: null,
        leasing: null,
        numUses: allowedUses,
        localTaxi: activeOptions.localTaxi,
        yearlyPool: true,
      })
    } else {
      setErrorState(checkErrors)
      dispatch(
        notificationsActions.generalPageError(
          'Make sure a valid amount of transactions, transaction limit is set and there is at least one service provider'
        )
      )
    }
  }

  if (!availableMerchants || !merchantsMap || !allGrhPrograms || !segmentsMap) {
    return <Loading fullPage />
  }

  if (allGrhPrograms[segmentId]) {
    return <Navigate to={`/guaranteed-rides/${segmentId}`} replace />
  }

  const noActiveOptions = Object.keys(activeOptions).every(
    (option) => !activeOptions[option]
  )

  return (
    <>
      <ColumnView defaultView>
        <Column>
          <Program.SegmentAndDateConfig
            currentSegment={segmentsMap[segmentId]}
            dropdownItems={SegmentDropdownUtils.segmentDropdownItems(
              allSegments,
              allGrhPrograms
            )}
            onDateChange={handleDateChange}
            onSegmentChange={handleSegmentChange}
          />
          <BudgetUsesSelector
            budget={transactionLimit}
            onBudgetChange={(newLimit) => setTransactionLimit(newLimit * 100)}
            recBudget={GRHUtils.recTransacLimit}
            uses={allowedUses}
            recUses={GRHUtils.recUses}
            onUsesChange={(newUses) => setAllowedUses(newUses)}
            budgetError={errorState.transactionLimit}
            usesError={errorState.uses}
          />
          <ProgramOption
            type='rideshare'
            active={activeOptions.rideshare}
            activeMerchantIds={activeRideShareIds}
            availableMerchantIds={availableMerchantIds}
            handleToggle={handleOptionToggle}
            handleOpenModal={handleOpenModal}
            error={errorState.rideshare}
          />
          <ProgramOption
            type='localTaxi'
            active={activeOptions.localTaxi}
            activeMerchantIds={activeLocalTaxiIds}
            availableMerchantIds={activeLocalTaxiIds}
            handleToggle={handleOptionToggle}
            handleOpenModal={handleOpenModal}
            error={errorState.localTaxi}
            readOnly
          />
        </Column>
        {/* Right */}
        <Column>
          <GRHActivationSummary
            startDate={startDate.text}
            currentSegment={segmentsMap[segmentId]}
            totalUses={allowedUses}
            transactionLimit={transactionLimit}
            activeOptions={activeOptions}
            activeMerchantIds={activeRideShareIds}
            availableMerchantIds={availableMerchantIds}
            handleOpenModal={handleOpenModal}
          />
        </Column>
      </ColumnView>
      <Program.Footer>
        <FlexContainer marginLeft='auto'>
          <Button
            variant='tertiary'
            marginRight='1rem'
            onClick={() => navigate(`/guaranteed-rides/${segmentId}`)}
          >
            Cancel
          </Button>
          <Button disabled={noActiveOptions} onClick={handleActivate}>
            Activate
          </Button>
        </FlexContainer>
      </Program.Footer>
      <ServiceProviderSelectorModal
        open={isModalOpen}
        title='Available Service Providers'
        closeModal={() => {
          setIsModalOpen(false)
          SegmentService.track('modes-modal-toggle', {
            action: 'close',
            state: 'edit',
            location: 'grh-providers',
          })
        }}
        activeProviders={activeRideShareIds}
        availableProviders={availableMerchantIds}
        onSave={(newActiveProviders: string[]) =>
          handleProviderChange(newActiveProviders)
        }
        modeTypeFilters
      />
    </>
  )
})

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  GRHActivationView.displayName = 'GRHActivationView'
}

export default GRHActivationView
